import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HideFooter, HideHeader, MetaDescription, MetaKeywords } from "@insite/client-framework/Types/FieldDefinition";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import Page from "@insite/mobius/Page";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Button from "@pacific/MobiusOverrides/Button/Button";
import systemColors from "@pacific/XDL/PreStyle/colors";
import { color, getAllColorsAsDropdownOptions, layoutContainer } from "@pacific/XDL/Utilities";
import React from "react";
import { css } from "styled-components";

export interface BlogPostStyles {
    blogHeader?: InjectableCss;
    blogHeaderContainer?: InjectableCss;
    bannerShadow?: TypographyPresentationProps;
}
export const blogPostStyles: BlogPostStyles = {
    blogHeader: {
        css: css`
            background-position: center center;
            background-repeat: no-repeat;
            background-size: cover;
            margin-bottom: 32px;
        `,
    },
    blogHeaderContainer: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: center;
            flex-direction: column;
            height: 300px;
            ${layoutContainer};
        `,
    },
    bannerShadow: {
        css: css`
            ${({ addShadow }: any) => {
                if (addShadow) {
                    return css`
                        text-shadow: ${color("common.ui7")} 0 0 12px;
                    `;
                }
            }}
        `,
    },
};
const styles = blogPostStyles;

const BlogPostPage: React.FunctionComponent<PageProps> = ({ id, fields }) => {
    const history = useHistory();
    const { isInShell } = useShellContext();
    const { url, title } = useGetLink(fields.buttonLink ?? { type: "Page", value: "" });
    const blogDate = new Date(fields.blogDate);

    const onClick = (url: string | undefined, openInNewTab: boolean) => {
        if (!url) {
            return;
        }

        if (fields.openInNewTab) {
            window.open(url);
            return;
        }

        if (url.startsWith("http")) {
            if (isInShell) {
                return;
            }

            window.location.href = url;
        } else {
            history.push(url);
        }
    };

    let bannerTextColor = fields.bannerTextColor;
    let addShadow = false;
    if (bannerTextColor === "primary.contrast.with.shadow") {
        addShadow = true;
        bannerTextColor = "#FFFFFF";
    }

    return (
        <Page fullWidth={[true, true, true, true, true]}>
            <StyledWrapper
                {...styles.blogHeader}
                style={{ backgroundColor: fields.backgroundColor, backgroundImage: `url(${fields.backgroundImage})` }}
            >
                <StyledWrapper {...styles.blogHeaderContainer}>
                    {fields.title && (
                        <Typography {...styles.bannerShadow} {...{addShadow}} variant="h2" color={bannerTextColor}>
                            {fields.title}
                        </Typography>
                    )}
                    {fields.blogDate && (
                        <Typography  {...styles.bannerShadow} {...{addShadow}} variant="h5" color={bannerTextColor}>
                            {blogDate.toLocaleString("default", { month: "long" })} {blogDate.getDate()},{" "}
                            {blogDate.getFullYear()}
                        </Typography>
                    )}
                    {(fields.buttonLabel || fields.buttonLink.value) && (
                        <Button
                            variant={fields.buttonVariant}
                            onClick={() => onClick(url, fields.openInNewTab)}
                            aria-label={fields.buttonLabel || title || url}
                        >
                            {fields.buttonLabel || title || url}
                        </Button>
                    )}
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper
                css={css`
                    ${layoutContainer}
                `}
            >
                <Zone contentId={id} zoneName="Content" />
            </StyledWrapper>
        </Page>
    );
};

const cardInfoTab = {
    displayName: "Card Info",
    sortOrder: 0,
};

const colorOptions = getAllColorsAsDropdownOptions(systemColors);
colorOptions.splice(2, 0, {displayName: 'primary.contrast with drop shadow', value: 'primary.contrast.with.shadow'});

const pageModule: PageModule = {
    component: BlogPostPage,
    definition: {
        hasEditableTitle: true,
        hasEditableUrlSegment: true,
        pageType: "Content",
        fieldDefinitions: [
            HideHeader,
            HideFooter,
            MetaKeywords,
            MetaDescription,
            {
                name: "blogDate",
                defaultValue: null,
                editorTemplate: "DateTimeField",
                fieldType: "General",
                isRequired: true,
            },
            {
                name: "cardSummary",
                defaultValue: "",
                editorTemplate: "MultilineTextField",
                fieldType: "Translatable",
                isRequired: true,
                tab: cardInfoTab,
            },
            {
                name: "cardImage",
                defaultValue: "",
                editorTemplate: "ImagePickerField",
                fieldType: "Translatable",
                tab: cardInfoTab,
            },
            {
                name: "bannerTextColor",
                editorTemplate: "DropDownField",
                options: colorOptions,
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: "background",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Image", value: "image" },
                    { displayName: "Color", value: "color" },
                ],
                hideEmptyOption: true,
                defaultValue: "image",
                fieldType: "General",
            },
            {
                name: "backgroundImage",
                defaultValue: "",
                editorTemplate: "ImagePickerField",
                fieldType: "Translatable",
                isVisible: item => item.fields.background === "image",
                isRequired: true,
            },
            {
                name: "backgroundColor",
                defaultValue: "",
                editorTemplate: "ColorPickerField",
                fieldType: "General",
                isVisible: item => item.fields.background === "color",
                isRequired: true,
            },
            {
                fieldType: "Translatable",
                name: "buttonLabel",
                editorTemplate: "TextField",
                displayName: "Button Label",
                defaultValue: "",
            },
            {
                fieldType: "General",
                name: "buttonLink",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
            },
            {
                name: "buttonVariant",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Primary", value: "primary" },
                    { displayName: "Secondary", value: "secondary" },
                    { displayName: "Tertiary", value: "tertiary" },
                ],
                hideEmptyOption: true,
                defaultValue: "primary",
                fieldType: "General",
            },
            {
                name: "openInNewTab",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default pageModule;

export const BlogPostPageContext = "BlogPostPage";
